/**
 * ProfileSyncBadge — Feb 28, 2026
 *
 * Tiny breadcrumb pill that fades into the header toolbar for ~2.2 seconds
 * after a Profile preference saves successfully. Gives the user a visible
 * "yes this is live across all your devices now" confirmation so they
 * never wonder again whether a toggle "took" or quietly failed.
 *
 * Listens to the `writeright:profile-saved` window event. Each
 * PersistedToggle / AutoStreamToggle / GenesisDotPhrasesToggle dispatches
 * this event after its fire-and-forget PUT resolves. The full Settings
 * modal Save Profile button also dispatches it.
 *
 * Failure path: when an event is dispatched with `detail.success === false`,
 * the badge renders a soft red "save failed" variant for a longer 3.2s so
 * the user notices and can retry.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Check, AlertTriangle } from 'lucide-react';

export const ProfileSyncBadge = () => {
  const [visible, setVisible] = useState(false);
  const [success, setSuccess] = useState(true);
  const [label, setLabel] = useState('Synced');
  const hideTimer = useRef(null);

  useEffect(() => {
    const onSaved = (e) => {
      const ok = e?.detail?.success !== false;
      const customLabel = e?.detail?.label;
      setSuccess(ok);
      setLabel(customLabel || (ok ? 'Synced across devices' : 'Save failed — retrying'));
      setVisible(true);
      if (hideTimer.current) clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => setVisible(false), ok ? 2200 : 3200);
    };
    window.addEventListener('writeright:profile-saved', onSaved);
    return () => {
      window.removeEventListener('writeright:profile-saved', onSaved);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  if (!visible) return null;

  const okPalette = {
    container: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300',
    iconBg: 'bg-emerald-500/25 text-emerald-300',
    pulseBg: 'bg-emerald-400',
  };
  const errPalette = {
    container: 'bg-rose-500/15 border-rose-500/40 text-rose-300',
    iconBg: 'bg-rose-500/25 text-rose-300',
    pulseBg: 'bg-rose-400',
  };
  const p = success ? okPalette : errPalette;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-semibold uppercase tracking-wide backdrop-blur-sm ${p.container} animate-in fade-in slide-in-from-top-1 duration-200`}
      data-testid="profile-sync-badge"
      data-state={success ? 'success' : 'error'}
    >
      {/* Subtle outward pulse ring on first render — fades after one cycle */}
      <span
        className={`pointer-events-none absolute inset-0 rounded-full ${p.pulseBg} opacity-0 animate-[ping_1.4s_ease-out_1]`}
        style={{ opacity: 0.25 }}
        aria-hidden="true"
      />
      <span className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-full ${p.iconBg}`}>
        {success ? <Check className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />}
      </span>
      <span className="leading-none whitespace-nowrap">{label}</span>
    </div>
  );
};

/**
 * Helper: dispatch the `writeright:profile-saved` event. Tiny module-level
 * helper so callers don't have to remember the event name. Catches errors
 * defensively because this is fire-and-forget and never blocks the save.
 */
export const announceProfileSaved = (success = true, label = '') => {
  try {
    window.dispatchEvent(new CustomEvent('writeright:profile-saved', {
      detail: { success, label },
    }));
  } catch { /* SSR / non-browser — ignore */ }
};

export default ProfileSyncBadge;
